import { i18n } from '../i18n.js';
/**
 * Tutorial UI Module
 * Manages step-by-step tutorial guidance during gameplay
 */

import { AudioSettings } from './audio-settings.js';
import { CardPlay } from './card-play.js';
import { Lobby } from './lobby.js';
import { Mulligan } from './mulligan.js';
import { Platform } from './platform.js';
import { SafeArea } from './safe-area.js';
import { StoryFeed } from './story-feed.js';
import { TutorialCues } from './tutorial-cues.js';
import { TutorialIntentGate } from './tutorial-intent-gate.js';
import { GameState } from '../game-state.js';

/**
 * A translation, or the English fallback when it is not resolved.
 *
 * `t()` answers with the KEY when a translation is missing or the file has not
 * loaded yet, and a key is truthy — so the older `t(...) || fallback` shape put
 * `game.processingAction` in front of the player. Treat key-returned as
 * unresolved, which is what the rest of the client already does.
 */
function resolvedText(key, fallback, replacements) {
    const translated = i18n?.t?.(key, replacements);
    return translated && translated !== key ? translated : fallback;
}

let Tutorial = {};

Tutorial = (function () {
    'use strict';

    // Tutorial state
    // The longest a coach mark waits for a manga on its way (tryShowTip).
    const MANGA_TIP_WAIT_MS = 4000;
    let isActive = false;
    let currentStep = 0;
    let tipElement = null;
    let activeTipTargetId = null;
    let activeTipPosition = 'center';
    let skipButton = null;
    let runToken = 0;
    const allowedClass = 'tutorial-allowed';
    const clickTargetClass = 'tutorial-click-target';
    const endTurnControlGapVmin = 2;
    let skipButtonPositionRaf = 0;
    let skipButtonPositionRetryTimer = null;
    let skipButtonPositionRetries = 0;
    let skipButtonPositionWatchersInstalled = false;
    let skipButtonResizeObserver = null;
    let skipButtonMutationObserver = null;
    const skipButtonObservedElements = new Set();
    /* DOCUMENT events only — the name is not decoration. `moonfall:languagechange`
       used to head this list and is dispatched by i18n on `window`, so
       registering it on `document` did nothing at all. `i18n-translated` is
       dispatched on `document` and fires on the same language switch. */
    const skipButtonPositionDocumentEvents = [
        'moonfall:i18n-translated',
    ];
    // Once a step's gated action fires, we suppress board-render-triggered
    // re-applies until `advance()` moves us to the next step. Without this,
    // the End Turn highlight keeps pulsing through the opponent's turn.
    let consumedStep = -1;
    // Element ids referenced by the currently visible tip; they receive the
    // soft "look here" treatment in the cue layer.
    let activeHighlightIds = [];
    /* THE ACTION INTENT LESSON.
       A commanded board Character opens the intent dialog, and inside the
       recording its three offers are authored copy rather than a model call.
       The lesson happens INSIDE a step, not as a step of its own: the drop at
       step 3 does not resolve until the player picks, and `cardAction` — the
       event that advances the step — only arrives afterwards. Deliberately not
       a numbered step, because a step number is the key in five coupled places
       (the tips array in eight files, forcedMoves, organicPhaseStartsAtStep,
       six literals in onGameEvent, and the recording's organicCheckpoint), and
       renumbering them to say one sentence is not a trade worth making.
       null while no dialog is open. */
    let activeIntentCue = null;
    // The free-practice tip is a one-time nudge: the finale can open the dialog
    // twice (Emberling and Thorn Guard, in either order) and repeating the same
    // sentence the second time is noise, not teaching.
    let freeIntentTipShown = false;

    // Load tutorial data (will be fetched from server or embedded)
    let tutorialData = null;

    function viewportUnitPx() {
        return Math.min(window.innerWidth, window.innerHeight) / 100;
    }

    function viewportMarginPx() {
        return Math.max(8, viewportUnitPx());
    }

    /* SafeArea (scripts/ui/safe-area.js) resolves env(safe-area-inset-*) to
       pixels. Degrade to a zero-inset viewport if it has not loaded, so tip
       placement never depends on script order. */
    function safeArea() {
        const api = SafeArea;
        if (api?.clamp && api?.box) return api;
        return {
            box: (extra = 0) => ({
                left: extra, top: extra,
                right: window.innerWidth - extra, bottom: window.innerHeight - extra,
            }),
            clamp: (left, top, width, height, extra = 0) => ({
                left: Math.max(extra, Math.min(window.innerWidth - width - extra, left)),
                top: Math.max(extra, Math.min(window.innerHeight - height - extra, top)),
            }),
        };
    }

    function copyForActiveInput(tip) {
        if (!tip) return '';
        const mobileUi = !!Platform?.isMobileUi?.();
        return mobileUi && tip.touchText ? tip.touchText : tip.text;
    }

    /**
     * Initialize the tutorial system
     */
    function init() {
        // Tutorial data will be set when game starts via setTutorialData
    }

    /**
     * Set tutorial data (called when game starts with isTutorial flag)
     */
    function setTutorialData(data) {
        tutorialData = data;
    }

    function normalizeStartStep(options = {}) {
        let rawStep = 0;
        if (typeof options === 'number') rawStep = options;
        else if (options && typeof options === 'object') rawStep = options.step;
        const step = Number(rawStep);
        return Number.isFinite(step) && step > 0 ? Math.floor(step) : 0;
    }

    function isResumeStart(options = {}) {
        return Boolean(options && typeof options === 'object' && options.resumed === true);
    }

    /**
     * Start the tutorial
     */
    function start(options = {}) {
        if (!tutorialData) {
            console.warn('[Tutorial] No tutorial data loaded');
            return;
        }

        const startStep = normalizeStartStep(options);
        const resumed = isResumeStart(options);
        // Anonymous funnel count: the tutorial runs before any account exists,
        // so PostHog (consent-gated) cannot see it. A resumed run is the same
        // visit continuing, not a new start.
        if (!resumed) window.MoonfallFunnel?.track?.('tutorial_started');
        const token = ++runToken;
        isActive = true;
        currentStep = startStep;
        consumedStep = -1;
        freeIntentTipShown = false;
        endIntentLesson();
        installIntentLessonListeners();
        // initialStory can arrive before gameStarted starts the tutorial. Clear
        // any transient narrative/card inspection synchronously so the first
        // coach mark never appears on top of it.
        StoryFeed?.dismissTransient?.();
        createSkipButton();

        const organicStart = tutorialData.organicPhaseStartsAtStep || 3;
        if (startStep >= organicStart) {
            setTimeout(() => {
                if (token !== runToken || !isActive) return;
                transitionToOrganic({ showCompletionTip: !resumed });
            }, 500);
            console.log('[Tutorial] Resumed at organic phase');
            return;
        }

        // Show first tip (mulligan) after a brief delay for the game to render
        // Note: Mulligan tip (step 0) should be shown when mulligan overlay appears
        setTimeout(() => {
            if (token !== runToken || !isActive) return;
            showTipForStep(currentStep);
            if (currentStep > 0) {
                applyForcedMoveRestrictions();
            }
            // No forced move restrictions during mulligan - player can choose freely.
        }, 500);

        console.log('[Tutorial] Started');
    }

    function canAdvanceToSpellStep() {
        const restriction = tutorialData?.forcedMoves?.['5'];
        const requiredCardId = restriction?.allowedCardId;
        if (!requiredCardId || typeof GameState === 'undefined' || !GameState?.get) {
            return true;
        }

        const state = GameState.get();
        const hand = Array.isArray(state?.player?.hand) ? state.player.hand : [];
        const spellCard = hand.find((card) => String(card?.id || '').toLowerCase() === String(requiredCardId).toLowerCase());
        if (!spellCard) {
            return false;
        }

        const availableMana = Number(state?.player?.mana ?? 0);
        const requiredMana = Number(spellCard?.manaCost ?? 0);
        return availableMana >= requiredMana;
    }

    /**
     * Advance to the next tutorial step
     */
    function advance() {
        if (!isActive) return;

        currentStep++;
        consumedStep = -1;
        console.log('[Tutorial] Advancing to step:', currentStep);

        const organicStart = tutorialData.organicPhaseStartsAtStep || 3;
        if (currentStep >= organicStart) {
            // Transition to organic play
            transitionToOrganic();
            return;
        }

        // Clear current tip and restrictions
        hideTip();
        clearHighlights();

        // Get the next tip to check for its target element
        const nextTip = tutorialData.tips?.find(t => t.step === currentStep);
        const targetId = nextTip?.target;
        // The step this advance owns: a later advance (a catch-up across
        // several steps) owns the tip, and this one must not mount it again —
        // every advance used to show the FINAL step's tip, once per step.
        const stepShown = currentStep;

        let mangaWaitStartedAt = null;

        // Helper function to show the tip when ready
        const tryShowTip = (retries = 0) => {
            // The delayed/retrying chain must die when the tutorial ends, or a
            // tip can resurrect over the lobby after end() (which clears isActive).
            if (!isActive) return;
            if (currentStep !== stepShown) return;

            // Scripted card stories use the same small reading surface as
            // coach marks. Let the finite reveal finish before mounting the
            // next instruction so neither one obscures the other.
            if (StoryFeed?.isTransientBusy?.()) {
                setTimeout(() => tryShowTip(retries), 100);
                return;
            }

            /* A manga on its way announces the turn that just ended, and its
               panel cannot open over a coach mark: mounted first, the tip held
               it back until the player acted on the tip. The enemy now waits
               for its messages to be read (owner, 28 Oct), so its turn ends
               with nothing queued and the tip won that race by a second. Let
               the manga land first, but never wait on it for long: a recorded
               panel lands within about a second. */
            if (StoryFeed?.mangaRevealPending?.()) {
                mangaWaitStartedAt ??= Date.now();
                if (Date.now() - mangaWaitStartedAt < MANGA_TIP_WAIT_MS) {
                    setTimeout(() => tryShowTip(retries), 100);
                    return;
                }
            }

            // Check if target element exists (unless it's 'center' positioned)
            const targetEl = targetId ? document.getElementById(targetId) : null;

            if (targetId && !targetEl && retries < 5) {
                // Target not found, retry after a delay
                console.log(`[Tutorial] Target "${targetId}" not found, retrying... (${retries + 1}/5)`);
                setTimeout(() => tryShowTip(retries + 1), 500);
                return;
            }

            showTipForStep(currentStep);
            applyForcedMoveRestrictions();
        };

        // Unlock the new step's allowed action NOW — waiting for the tip's
        // 800ms delay leaves the previous step's pointer-events lock active,
        // swallowing fast clicks (e.g. End Turn right after summoning).
        applyForcedMoveRestrictions();

        // Show the tip after a brief delay for game state to update
        setTimeout(tryShowTip, 800);
    }

    /**
     * Handle game events to trigger tutorial steps
     */
    function onGameEvent(eventName, data) {
        if (!isActive) return;

        console.log(`[Tutorial] Event: ${eventName}, step: ${currentStep}`, data);

        // Map events to step transitions
        switch (eventName) {
            case 'mulliganComplete':
                if (currentStep === 0) {
                    console.log('[Tutorial] Advancing from step 0 (mulligan) to step 1 (summon)');
                    // Explicitly hide mulligan tip immediately
                    hideTip();
                    clearHighlights();
                    advance(); // Move from "mulligan" to "summon"
                }
                break;

            case 'summonCharacter':
                if (currentStep === 1) {
                    consumedStep = 1;
                    console.log('[Tutorial] Advancing from step 1 (summon) to step 2 (end turn)');
                    advance(); // Move from "summon" to "end turn"
                }
                break;

            case 'endTurn':
                if (currentStep === 2) {
                    // After first end turn, wait for turn 2 start
                    console.log('[Tutorial] End turn at step 2, waiting for turn 2 start');
                    consumedStep = 2;
                    hideTip();
                    clearForcedMoveRestrictions();
                    clearHighlights(); // Explicitly clear highlights during enemy turn
                } else if (currentStep === 4) {
                    console.log('[Tutorial] Recorded attack complete; waiting for the second enemy turn');
                    consumedStep = 4;
                    hideTip();
                    clearForcedMoveRestrictions();
                    clearHighlights();
                } else if (currentStep === 6) {
                    console.log('[Tutorial] Final turn submitted; waiting for the recorded manga panel');
                    consumedStep = 6;
                    hideTip();
                    clearForcedMoveRestrictions();
                    clearHighlights();
                }
                break;

            case 'enemyTurnStart':
                // Always clear highlights during enemy turn
                clearHighlights();
                activeHighlightIds = [];
                refreshCues();
                break;

            case 'turnStart':
                // After mulligan ends and it becomes player's turn, ensure step 1 tip is shown
                if (currentStep === 1 && data && data.turnNumber === 1) {
                    console.log('[Tutorial] Turn 1 started at step 1, showing summon tip');
                    // Re-show the tip in case it wasn't shown yet
                    showTipForStep(1);
                    applyForcedMoveRestrictions();
                } else if (currentStep === 2 && data && data.turnNumber >= 2) {
                    console.log('[Tutorial] Advancing from step 2 (end turn) to step 3 (attack)');
                    advance(); // Move to "attack" step
                } else if (currentStep === 4 && data && data.turnNumber >= 5) {
                    if (!canAdvanceToSpellStep()) {
                        console.log('[Tutorial] Spell step waiting for enough mana');
                        break;
                    }
                    console.log('[Tutorial] Spell step ready, showing spell tip');
                    advance(); // Move to spell tip (step 5)
                }
                break;

            case 'attack':
            case 'playAction':
            case 'cardAction':
                if (currentStep === 3) {
                    consumedStep = 3;
                    console.log('[Tutorial] Advancing from step 3 (attack) to step 4 (second End Turn)');
                    advance();
                } else if (currentStep === 5) {
                    consumedStep = 5;
                    // Player cast the spell, show targeting tip
                    console.log('[Tutorial] Spell cast, showing targeting tip');
                    advance(); // Move to targeting tip (step 6)
                }
                break;
        }
    }


    /**
     * The Action Intent dialog has just painted its three offers.
     *
     * Strictly downstream: this reads what the dialog decided and dresses it.
     * Nothing here can change which offers exist, which one is takeable, or
     * what the pick commits — the recording owns all three, and the server
     * enforces them.
     */
    function beginIntentLesson(detail) {
        if (!isActive || !tutorialData) return;
        /* ONLY WHILE THE SCRIPT IS STILL RUNNING.
           A signed-in player continues into ORGANIC play in the same game, and
           the tutorial stays "active" after the handoff so its exit control
           survives (`currentStep` just becomes 999). Without this guard the
           first live, paid intent of that organic match would be met by the
           free-practice coach mark — a tutorial lesson on a real turn. The gate
           is the precise signal: it is set from the current step's
           `requiresIntent`, and `transitionToOrganic` clears it. */
        if (!TutorialIntentGate.isExpected()) return;
        const locked = Array.isArray(detail?.lockedOptionIndexes) ? detail.lockedOptionIndexes : [];
        const optionCount = Number.isInteger(detail?.optionCount) ? detail.optionCount : 0;
        // The taught moment is the one with exactly one offer open. Derived
        // rather than announced, so the client cannot disagree with the server
        // about which button it is pointing at.
        const openIndexes = [];
        for (let index = 0; index < optionCount; index += 1) {
            if (!locked.includes(index)) openIndexes.push(index);
        }
        const taught = locked.length > 0 && openIndexes.length === 1;
        activeIntentCue = {
            allowedIndex: taught ? openIndexes[0] : null,
            // The commanded character, so the veil can keep it lit.
            sourceCardId: typeof detail?.sourceCardId === 'string' ? detail.sourceCardId : null,
        };
        // Raises the cue layer and the coach mark above the dialog (10009);
        // without it a ring on an option button paints underneath the very
        // panel it is pointing into.
        document.body.classList.add('tutorial-intent-choice');

        const tip = taught
            ? tutorialData.intentTips?.taught
            : (freeIntentTipShown ? null : tutorialData.intentTips?.free);
        if (tip) {
            if (!taught) freeIntentTipShown = true;
            // Anchored to the dialog's own id, not a class: the placement probe
            // is `getElementById` only and a class target burns five 500ms
            // retries first.
            showTip(copyForActiveInput(tip), 'action-intent-panel', 'above', 0, []);
        } else {
            refreshCues();
        }
    }

    /** The dialog is gone — answered, failed, or closed by a turn boundary. */
    function endIntentLesson() {
        const had = !!activeIntentCue;
        activeIntentCue = null;
        document.body.classList.remove('tutorial-intent-choice');
        if (!had) return;
        /* Only tear down OUR tip. The dialog settles at the same moment the
           card action completes, and that completion is what advances the
           step — so the next step's coach mark can already be on screen by the
           time this runs. An unconditional `hideTip()` here would delete it. */
        if (isActive && activeTipTargetId === 'action-intent-panel') hideTip();
        else refreshCues();
    }

    let intentLessonListenersInstalled = false;

    function installIntentLessonListeners() {
        if (intentLessonListenersInstalled) return;
        intentLessonListenersInstalled = true;
        window.addEventListener('moonfall:action-intent-options', (event) => {
            beginIntentLesson(/** @type {CustomEvent} */ (event)?.detail);
        });
        window.addEventListener('moonfall:action-intent-settled', () => {
            endIntentLesson();
        });
    }

    /**
     * Transition to organic (normal) gameplay
     */
    function transitionToOrganic({ showCompletionTip = true } = {}) {
        window.MoonfallFunnel?.track?.('tutorial_free_play');
        // Show final transition tip
        const transitionTip = tutorialData.tips?.find(t => t.event === 'organicPhase');
        if (transitionTip && showCompletionTip) {
            showTip(copyForActiveInput(transitionTip), null, 'center', transitionTip.autoHide || 5000);
        } else {
            hideTip();
        }

        // Clear all restrictions
        clearForcedMoveRestrictions();

        // Keep tutorial "active" for skip button but no more forced moves
        currentStep = 999; // High number to indicate organic phase

        console.log('[Tutorial] Transitioned to organic play');
    }

    function completeRecordedPhase() {
        if (!isActive || currentStep !== 6) return false;
        transitionToOrganic();
        return true;
    }

    /**
     * Show tip for a specific step
     */
    function showTipForStep(step) {
        if (!tutorialData || !tutorialData.tips) return;

        const tip = tutorialData.tips.find(t => t.step === step);
        if (tip) {
            // For spell intro step (5), dynamically find and highlight the spell card
            let highlights = tip.highlight || [];
            if (step === 5) {
                // Find spell cards in hand and highlight the first one
                const spellCard = document.querySelector('#player-hand .card.spell-card');
                if (spellCard && spellCard.id) {
                    highlights = [spellCard.id];
                    console.log('[Tutorial] Found spell card to highlight:', spellCard.id);
                }
            }
            showTip(copyForActiveInput(tip), tip.target, tip.position, tip.autoHide, highlights);
        }
    }

    /**
     * Show a tutorial tip
     * @param {string} text - Tip text (supports **bold**)
     * @param {string} targetId - Element ID to position near
     * @param {string} position - Position relative to target
     * @param {number} autoHideMs - Auto-hide after ms (0 = no auto-hide)
     * @param {string[]} highlight - Array of element IDs to highlight
     */
    function showTip(text, targetId, position = 'center', autoHideMs = 0, highlight = []) {
        hideTip({ reseat: false });

        // Apply highlights to referenced elements
        clearHighlights();
        if (highlight && highlight.length > 0) {
            highlight.forEach(id => {
                const el = document.getElementById(id) || document.querySelector(`.${id}`);
                if (el) {
                    el.classList.add('tutorial-highlight');
                }
            });
        }

        tipElement = document.createElement('div');
        tipElement.className = 'tutorial-tip';
        tipElement.classList.add(`position-${position}`);

        // Build the tip body with DOM nodes (textContent is XSS-safe by
        // construction); **bold** segments become <strong> elements.
        const textSpan = document.createElement('span');
        textSpan.className = 'tutorial-tip-text';
        String(text).split(/\*\*(.+?)\*\*/g).forEach((segment, index) => {
            if (!segment) return;
            if (index % 2 === 1) {
                const strong = document.createElement('strong');
                strong.textContent = segment;
                textSpan.appendChild(strong);
            } else {
                textSpan.appendChild(document.createTextNode(segment));
            }
        });

        tipElement.appendChild(textSpan);

        const dismissButton = document.createElement('button');
        dismissButton.className = 'tutorial-tip-dismiss';
        dismissButton.textContent = resolvedText('game.gotIt', 'Got it');
        dismissButton.setAttribute('data-i18n', 'game.gotIt');
        dismissButton.addEventListener('click', hideTip);
        tipElement.appendChild(dismissButton);

        activeHighlightIds = Array.isArray(highlight) ? highlight.slice() : [];
        activeTipTargetId = targetId || null;
        activeTipPosition = position;

        document.body.appendChild(tipElement);
        ensureSkipButtonObservedElements();

        // Position the tip relative to target element
        if (targetId && position !== 'center') {
            positionTipNearElement(targetId, position);
        }
        refreshCues();
        positionSkipButton();
        scheduleSkipButtonPosition();

        // Auto-hide if specified. Compare identity, not existence — a stale
        // timer from a previous tip must never kill its successor.
        if (autoHideMs > 0) {
            const shownTip = tipElement;
            setTimeout(() => {
                if (tipElement === shownTip) hideTip();
            }, autoHideMs);
        }
    }

    /**
     * Clear all tutorial highlights
     */
    function clearHighlights() {
        document.querySelectorAll('.tutorial-highlight').forEach(el => {
            el.classList.remove('tutorial-highlight');
        });
    }

    function markAllowedTarget(el, { clickTarget = false } = {}) {
        if (!el) return;
        el.classList.add(allowedClass);
        if (clickTarget) {
            el.classList.add(clickTargetClass);
        }
    }

    function clearForcedMoveMarkers() {
        document.querySelectorAll(`.${allowedClass}, .${clickTargetClass}`).forEach(el => {
            el.classList.remove(allowedClass);
            el.classList.remove(clickTargetClass);
        });
    }


    /**
     * Position tip near a target element
     */
    function positionTipNearElement(targetId, position) {
        const target = document.getElementById(targetId) || document.querySelector(`.${targetId}`);
        if (!target || !tipElement) return;

        const targetRect = target.getBoundingClientRect();
        const tipRect = tipElement.getBoundingClientRect();
        const placement = resolveTipPlacement(targetRect, tipRect, position);
        if (!placement) return;

        tipElement.style.top = `${placement.top}px`;
        tipElement.style.left = `${placement.left}px`;
        tipElement.style.right = 'auto';
        tipElement.style.bottom = 'auto';
        tipElement.style.transform = 'none';
    }

    function tipPlacementFor(targetRect, tipRect, position) {
        const horizontalCenter = targetRect.left + (targetRect.width / 2) - (tipRect.width / 2);
        const verticalCenter = targetRect.top + (targetRect.height / 2) - (tipRect.height / 2);
        const gap = 2 * viewportUnitPx();
        const placements = {
            above: { left: horizontalCenter, top: targetRect.top - tipRect.height - gap },
            below: { left: horizontalCenter, top: targetRect.bottom + gap },
            left: { left: targetRect.left - tipRect.width - gap, top: verticalCenter },
            right: { left: targetRect.right + gap, top: verticalCenter },
        };
        return placements[position] ? { ...placements[position], position } : null;
    }

    function tipPositionOrder(position) {
        const fallbacks = {
            above: ['above', 'below', 'left', 'right'],
            below: ['below', 'above', 'left', 'right'],
            left: ['left', 'right', 'above', 'below'],
            right: ['right', 'left', 'above', 'below'],
        };
        return fallbacks[position] || [];
    }

    function getTipBlockers() {
        const blockerPadding = Math.max(4, 0.8 * viewportUnitPx());
        const selectors = [
            '#end-turn-btn',
            '#opponent-portrait',
            '#player-portrait',
            '#mulligan-cards',
            '.mulligan-card-wrapper',
            '.mulligan-header',
            '#mulligan-confirm-btn',
            '#story-feed-popup:not([hidden])',
            /* The intent dialog paints at 10009, far above this tip (10003).
               Without it here, `findBestPlacement` reports success for a slot
               the dialog completely covers — and on a landscape phone page box
               the dialog owns most of the bottom strip, which is exactly where
               a tip anchored to End Turn wants to sit. */
            '#action-intent-panel:not(.is-minimized)',
            /* And the chip that brings a minimized dialog back: it is seated
               above the hand, exactly where a tip anchored to the board wants
               to sit, and it is the player's only way back into the intent. */
            '#action-intent-restore',
            /* And the tutorial's own way out. On a landscape phone Skip sits in
               the top-left, which is also the first of the four corner
               fallbacks below — measured at 915x336 and 852x344 after the
               Emberling summon, the tip landed at the safe box's top-left and
               printed 4915px² over the button. A coach mark must never hide the
               control that dismisses the whole lesson. */
            '#skip-tutorial-btn',
        ];
        return selectors.flatMap((selector) => {
            return Array.from(document.querySelectorAll(selector)).flatMap((element) => {
                const rect = element.getBoundingClientRect();
                if (rect.width === 0 || rect.height === 0) return [];
                return [{
                    left: rect.left - blockerPadding,
                    right: rect.right + blockerPadding,
                    top: rect.top - blockerPadding,
                    bottom: rect.bottom + blockerPadding,
                }];
            });
        });
    }

    function tipOverlapArea(placement, width, height, blockers) {
        const rect = rectForPlacement(placement, width, height);
        return blockers.reduce((total, blocker) => {
            const overlapWidth = Math.max(0, Math.min(rect.right, blocker.right) - Math.max(rect.left, blocker.left));
            const overlapHeight = Math.max(0, Math.min(rect.bottom, blocker.bottom) - Math.max(rect.top, blocker.top));
            return total + (overlapWidth * overlapHeight);
        }, 0);
    }

    function resolveTipPlacement(targetRect, tipRect, position) {
        const margin = viewportMarginPx();
        const directional = tipPositionOrder(position)
            .map((candidatePosition) => tipPlacementFor(targetRect, tipRect, candidatePosition))
            .filter(Boolean)
            .map((placement) => clampPlacementToViewport(placement, tipRect.width, tipRect.height));
        const safe = safeArea().box(margin);
        const corners = [
            { left: safe.left, top: safe.top, position },
            { left: safe.left, top: safe.bottom - tipRect.height, position },
            { left: safe.right - tipRect.width, top: safe.top, position },
            { left: safe.right - tipRect.width, top: safe.bottom - tipRect.height, position },
        ].map((placement) => clampPlacementToViewport(placement, tipRect.width, tipRect.height));
        const seen = new Set();
        const candidates = directional.concat(corners).filter((placement) => {
            const key = `${placement.left}:${placement.top}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });
        const blockers = getTipBlockers();
        return candidates.find((placement) => (
            !blockers.some((blocker) => rectsOverlap(
                rectForPlacement(placement, tipRect.width, tipRect.height),
                blocker
            ))
        )) || candidates.reduce((best, candidate) => {
            if (!best) return candidate;
            return tipOverlapArea(candidate, tipRect.width, tipRect.height, blockers)
                < tipOverlapArea(best, tipRect.width, tipRect.height, blockers)
                ? candidate
                : best;
        }, null);
    }

    /**
     * Hide the current tip. `{ reseat: false }`: the caller shows the next one
     * in this same task and solves the seat then — a solve for a screen with
     * no coach mark would never be painted. (Also a click listener: an event
     * argument re-seats as before.)
     */
    function hideTip(options) {
        if (tipElement && tipElement.parentNode) {
            tipElement.parentNode.removeChild(tipElement);
        }
        tipElement = null;
        activeTipTargetId = null;
        activeTipPosition = 'center';
        clearHighlights();
        activeHighlightIds = [];
        refreshCues();
        if (options?.reseat !== false) positionSkipButton();
    }

    /**
     * Rebuild the cue-layer spec from the current tutorial state.
     * Idempotent and cheap — safe to call after every board render,
     * mulligan selection change or tip transition. The cue layer diffs
     * by anchor identity so unchanged cues keep their animations.
     */
    function refreshCues() {
        const cuesApi = TutorialCues;
        if (!cuesApi?.apply) return;

        if (!isActive) {
            cuesApi.clear();
            return;
        }

        const cueSpecs = [];
        const actionEls = [];
        const restriction = tutorialData?.forcedMoves?.[String(currentStep)];

        /* THE INTENT DIALOG OWNS THE CUE LAYER WHILE IT IS OPEN.
           The drop has happened but `cardAction` has not arrived, so
           `consumedStep` is still behind `currentStep` and the step's own cue
           — a drag comet from Emberling to the enemy portrait — would keep
           pulsing at a board the player is no longer being asked to touch.
           Only the taught moment draws a ring: when every offer is takeable
           there is no single right answer to point at, and three rings with
           three "Click" pills is clutter, not guidance. */
        if (activeIntentCue) {
            const openOption = Number.isInteger(activeIntentCue.allowedIndex)
                ? document.getElementById(`action-intent-option-${activeIntentCue.allowedIndex}`)
                : null;
            /* KEEP THE COMMANDED CHARACTER LIT.
               The veil darkens the whole board while the dialog is up, so
               without this the card the question is ABOUT is dimmed exactly
               like every card it is not about — and nothing on the board
               connects the drag the player just made to the offers in front of
               them. A veil hole is the whole fix: presentation only, inside
               the lesson only, and it leaves live matches untouched. */
            const commanded = activeIntentCue.sourceCardId
                ? document.querySelector(
                    `#player-board [data-instance-id="${activeIntentCue.sourceCardId}"], `
                    + `#player-board [data-card-id="${activeIntentCue.sourceCardId}"]`,
                )
                : null;
            if (commanded) actionEls.push(commanded);
            if (openOption) {
                /* RING ONLY — no "Click" pill. The pill is laid out ABOVE its
                   anchor, and inside a three-row dialog that puts it squarely
                   on top of the offer above, which is one of the LOCKED ones:
                   it reads as a label for the button the player cannot take.
                   The ring marks the target and the coach mark directly above
                   already says to click it, so the pill adds nothing but that
                   ambiguity. Same call the End Turn cue makes for the same
                   reason. */
                cueSpecs.push({ type: 'click', anchor: openOption, pill: false });
                actionEls.push(openOption);
            }
            cuesApi.apply({ cues: cueSpecs, veil: actionEls.length > 0 ? actionEls : null });
            return;
        }

        if (restriction && consumedStep !== currentStep) {
            if (restriction.allowedAction === 'summon' || restriction.allowedAction === 'spell') {
                /* DRAGGED, never clicked (owner, "Moonfall Drag Play Brief",
                   2026-09-24): the lesson draws the gesture it teaches — a comet
                   from the card to where it lands. A summon lands in the slot
                   the server will seat it in, ringed and lit. A spell with no
                   target is cast by a release anywhere above the hand, so its
                   comet runs up to the middle of the table and nothing there is
                   ringed or lit (owner, 2026-09-25: "i do not like there being a
                   drawn area where it needs to be dragged"). */
                const isSpell = restriction.allowedAction === 'spell';
                const destination = isSpell
                    ? document.getElementById('game-board-middle')
                    : (CardPlay.landingSlot() || document.getElementById('player-board'));
                document.querySelectorAll(`#player-hand .card.${allowedClass}`).forEach((card) => {
                    if (destination) {
                        cueSpecs.push(isSpell
                            ? { type: 'drag', from: card, to: destination, target: false }
                            : { type: 'drag', from: card, to: destination });
                    }
                    actionEls.push(card);
                });
                if (destination && !isSpell) actionEls.push(destination);
            } else if (restriction.allowedAction === 'endTurn') {
                const endTurnBtn = document.getElementById('end-turn-btn');
                if (endTurnBtn) {
                    // The button already says "End Turn" — a "Click" pill on
                    // top of it would be double labeling.
                    cueSpecs.push({ type: 'click', anchor: endTurnBtn, pill: false });
                    actionEls.push(endTurnBtn);
                }
            } else if (restriction.allowedAction === 'attack') {
                const portrait = document.getElementById('opponent-portrait');
                if (portrait) {
                    document.querySelectorAll(`#player-board .card.${allowedClass}`).forEach((card) => {
                        cueSpecs.push({ type: 'drag', from: card, to: portrait });
                        actionEls.push(card);
                    });
                    actionEls.push(portrait);
                }
            }

            if (restriction.optionalSummonCardId) {
                document.querySelectorAll(`#player-hand .card.${allowedClass}`).forEach((card) => {
                    if (
                        card.dataset.cardId?.includes(restriction.optionalSummonCardId)
                        && !actionEls.includes(card)
                    ) {
                        actionEls.push(card);
                    }
                });
            }
        }

        // Mulligan step: guide the two forced discards, then the confirm
        // button once both are selected. (No veil — the mulligan overlay
        // already isolates itself from the board.)
        const inMulligan = currentStep === 0 && Mulligan?.isActive?.();
        if (inMulligan) {
            const unselected = document.querySelectorAll(
                '#mulligan-cards .mulligan-card-wrapper.tutorial-forced-select:not(.selected)'
            );
            if (unselected.length > 0) {
                unselected.forEach((wrapper) => {
                    // No ring here: the mulligan card overflows its wrapper,
                    // so a wrapper-fitted ring slices through the art. The
                    // wrapper's own gold breathing frame marks the card;
                    // the chevron + pill point at it.
                    cueSpecs.push({ type: 'click', anchor: wrapper, ring: false });
                    actionEls.push(wrapper);
                });
            } else {
                const confirmBtn = document.getElementById('mulligan-confirm-btn');
                if (confirmBtn) {
                    cueSpecs.push({ type: 'click', anchor: confirmBtn, pill: false });
                    actionEls.push(confirmBtn);
                }
            }
        }

        // Tip highlights that are not already action targets get the soft
        // "look here" shimmer; while veiled they also earn a veil hole.
        const lookEls = [];
        activeHighlightIds.forEach((id) => {
            const el = document.getElementById(id) || document.querySelector(`.${id}`);
            if (el && !actionEls.includes(el)) {
                cueSpecs.push({ type: 'look', anchor: el });
                lookEls.push(el);
            }
        });

        const veilEls = actionEls.length > 0 && !inMulligan ? actionEls.concat(lookEls) : null;
        cuesApi.apply({ cues: cueSpecs, veil: veilEls });
    }

    /**
     * Apply forced move restrictions based on current step
     */
    function syncIntentGate(restriction) {
        /* A PREDICTION FOR THE CLIENT, NEVER A PERMISSION.
           The server decides whether an action takes the intent path and
           answers it out of the recording; this only stops the client asking
           on a step the script has no offers for, which would earn a refusal
           and leave the player on a spinner. It is also what lets a signed-out
           visitor see the dialog at all — outside the script,
           `shouldRequestForDrop` still refuses a guest outright, because
           outside the script the offers cost money. */
        TutorialIntentGate.set(
            restriction?.requiresIntent === true && consumedStep !== currentStep
        );
    }

    function applyForcedMoveRestrictions() {
        if (!tutorialData || !tutorialData.forcedMoves) {
            TutorialIntentGate.reset();
            refreshCues();
            return;
        }

        // Suppress board-render-triggered re-applies between consumed action
        // and `advance()` (e.g., during the AI's turn after End Turn at step 2).
        if (consumedStep === currentStep) {
            clearForcedMoveRestrictions();
            return;
        }

        const restriction = tutorialData.forcedMoves[String(currentStep)];
        syncIntentGate(restriction);
        console.log('[Tutorial] Applying restrictions for step', currentStep, restriction);
        if (!restriction) {
            clearForcedMoveRestrictions();
            return;
        }

        const gameView = document.getElementById('game-view');
        if (!gameView) return;

        gameView.classList.add('tutorial-forced');

        // Clear previous allowed markers
        clearForcedMoveMarkers();

        // Mark allowed elements
        if (restriction.allowedAction === 'summon' && restriction.allowedCardId) {
            // Find the card in hand with matching ID
            document.querySelectorAll('#player-hand .card').forEach(card => {
                const cardId = card.dataset.cardId || '';
                if (cardId.startsWith(restriction.allowedCardId)) {
                    markAllowedTarget(card);
                }
            });
        } else if (restriction.allowedAction === 'endTurn') {
            const endTurnBtn = document.getElementById('end-turn-btn');
            markAllowedTarget(endTurnBtn, { clickTarget: true });
        } else if (restriction.allowedAction === 'attack' && restriction.allowedSourcePattern) {
            // Find the card on board matching pattern
            document.querySelectorAll('#player-board .card').forEach(card => {
                const cardId = card.dataset.cardId || '';
                if (cardId.includes(restriction.allowedSourcePattern)) {
                    markAllowedTarget(card);
                }
            });

            // If attack is restricted to opponent portrait, add class to grey out other targets
            if (restriction.allowedTargetType === 'opponentPortrait') {
                gameView.classList.add('tutorial-attack-portrait');
            }
        } else if (restriction.allowedAction === 'spell' && restriction.allowedCardId) {
            // Find the spell card in hand with matching ID
            document.querySelectorAll('#player-hand .card').forEach(card => {
                const cardId = card.dataset.cardId || '';
                if (cardId.toLowerCase().includes(restriction.allowedCardId.toLowerCase())) {
                    markAllowedTarget(card);
                }
            });
        } else if (restriction.allowedAction === 'finale') {
            const allowedIds = new Set(restriction.allowedSourceCardIds || []);
            document.querySelectorAll('#player-board .card').forEach(card => {
                const cardId = card.dataset.cardId || '';
                if (Array.from(allowedIds).some(allowedId => cardId.includes(allowedId))) {
                    markAllowedTarget(card);
                }
            });
            markAllowedTarget(document.getElementById('opponent-portrait'));
            markAllowedTarget(document.getElementById('end-turn-btn'), { clickTarget: true });
            if (restriction.allowedTargetType === 'opponentPortrait') {
                gameView.classList.add('tutorial-attack-portrait');
            }
        }

        if (restriction.optionalSummonCardId) {
            document.querySelectorAll('#player-hand .card').forEach(card => {
                const cardId = card.dataset.cardId || '';
                if (cardId.includes(restriction.optionalSummonCardId)) {
                    markAllowedTarget(card);
                }
            });
        }

        refreshCues();
    }

    /**
     * Clear all forced move restrictions
     */
    function clearForcedMoveRestrictions() {
        TutorialIntentGate.reset();
        const gameView = document.getElementById('game-view');
        if (gameView) {
            gameView.classList.remove('tutorial-forced');
            gameView.classList.remove('tutorial-attack-portrait');
        }

        clearForcedMoveMarkers();
        refreshCues();
    }

    function positionSkipButton() {
        if (!skipButton) return true;

        if (AudioSettings?.reposition) {
            AudioSettings.reposition();
            skipButton.style.visibility = 'visible';
            skipButton.dataset.layout = 'end-turn-above';
            return true;
        }

        const endTurnBtn = document.getElementById('end-turn-btn');
        if (!endTurnBtn) return false;

        const endTurnRect = endTurnBtn.getBoundingClientRect();
        const skipRect = skipButton.getBoundingClientRect();
        if (endTurnRect.width === 0 || endTurnRect.height === 0 || skipRect.width === 0 || skipRect.height === 0) return false;

        const vmin = viewportUnitPx();
        const gapPx = vmin * endTurnControlGapVmin;
        const preferredLeft = endTurnRect.left + (endTurnRect.width / 2) - (skipRect.width / 2);
        const preferredTop = endTurnRect.top - gapPx - skipRect.height;
        const initialPlacement = clampPlacementToViewport(
            { left: preferredLeft, top: preferredTop, mode: 'end-turn' },
            skipRect.width,
            skipRect.height
        );
        const placement = avoidSurfaceCollision(
            initialPlacement.left,
            initialPlacement.top,
            skipRect.width,
            skipRect.height,
            gapPx
        );
        if (!placement) {
            skipButton.style.visibility = 'hidden';
            return false;
        }

        skipButton.style.left = `${Math.round(placement.left)}px`;
        skipButton.style.top = `${Math.round(placement.top)}px`;
        skipButton.style.right = 'auto';
        skipButton.style.bottom = 'auto';
        skipButton.style.transform = 'none';
        skipButton.style.visibility = 'visible';
        skipButton.dataset.layout = placement.mode || 'end-turn';
        skipButtonPositionRetries = 0;
        if (skipButtonPositionRetryTimer) {
            clearTimeout(skipButtonPositionRetryTimer);
            skipButtonPositionRetryTimer = null;
        }
        return true;
    }

    function avoidSurfaceCollision(left, top, width, height, gapPx) {
        const defaultPlacement = { left, top, mode: 'end-turn' };
        const blockers = getSkipButtonBlockers();
        const defaultRect = rectForPlacement(defaultPlacement, width, height);
        const overlappingBlockers = blockers.filter((blocker) => rectsOverlap(defaultRect, blocker.rect));
        if (overlappingBlockers.length === 0) {
            return defaultPlacement;
        }

        // Preserve the original Skip Tutorial slot above End Turn. Resolve a
        // rare collision by staying on the same axis; the tutorial tip and
        // StoryFeed keep their own established placement and never move for it.
        const candidates = overlappingBlockers.flatMap((blocker) => ([
            { left, top: blocker.rect.top - gapPx - height, mode: 'end-turn' },
            { left, top: blocker.rect.bottom + gapPx, mode: 'end-turn' },
        ]));

        const margin = viewportMarginPx();
        const viewportRight = window.innerWidth - margin;
        const viewportBottom = window.innerHeight - margin;
        const safeCandidate = candidates.find((candidate) => {
            const rect = rectForPlacement(candidate, width, height);
            return rect.left >= margin
                && rect.top >= margin
                && rect.right <= viewportRight
                && rect.bottom <= viewportBottom
                && !blockers.some((blocker) => rectsOverlap(rect, blocker.rect));
        });

        if (safeCandidate) return safeCandidate;

        // On the shortest phones, the right edge can be continuously occupied
        // by opponent portrait → coach mark → player portrait, leaving no
        // 44px anchored slot. Fall back to a true safe corner rather than
        // returning the known-overlapping position or drifting over cards.
        const cornerCandidates = [
            { left: margin, top: margin, mode: 'safe-corner' },
            { left: margin, top: window.innerHeight - height - margin, mode: 'safe-corner' },
            { left: window.innerWidth - width - margin, top: margin, mode: 'safe-corner' },
            {
                left: window.innerWidth - width - margin,
                top: window.innerHeight - height - margin,
                mode: 'safe-corner',
            },
        ];
        return cornerCandidates.find((candidate) => {
            const rect = rectForPlacement(candidate, width, height);
            return !blockers.some((blocker) => rectsOverlap(rect, blocker.rect));
        }) || null;
    }

    function getSkipButtonBlockers() {
        const selectors = [
            { selector: '#end-turn-btn', type: 'end-turn' },
            { selector: '.tutorial-tip', type: 'tip' },
            { selector: '#opponent-portrait', type: 'portrait' },
            { selector: '#player-portrait', type: 'portrait' },
            { selector: '#mulligan-cards', type: 'mulligan' },
            { selector: '.mulligan-card-wrapper', type: 'mulligan' },
            { selector: '.mulligan-header', type: 'mulligan' },
            { selector: '#mulligan-confirm-btn', type: 'mulligan' },
            { selector: '#story-feed-popup:not([hidden])', type: 'story' },
            { selector: '#audio-volume-control', type: 'audio' },
            // Same reason as the tip's list: Skip would otherwise be parked
            // underneath the intent dialog and report itself placed.
            { selector: '#action-intent-panel:not(.is-minimized)', type: 'intent' },
            { selector: '#action-intent-restore', type: 'intent' },
        ];

        return selectors.flatMap(({ selector, type }) => {
            return Array.from(document.querySelectorAll(selector)).flatMap((el) => {
                const rect = el.getBoundingClientRect();
                if (rect.width === 0 || rect.height === 0) return [];
                return [{ type, rect }];
            });
        });
    }

    function clampPlacementToViewport(placement, width, height) {
        // Clamp into the SAFE area, not the raw viewport: with viewport-fit=cover
        // a landscape phone hides ~59px behind the notch on one side, and a tip
        // parked at `margin` was physically cut in half there.
        const clamped = safeArea().clamp(placement.left, placement.top, width, height, viewportMarginPx());
        return { ...placement, ...clamped };
    }

    function rectForPlacement(placement, width, height) {
        return {
            left: placement.left,
            right: placement.left + width,
            top: placement.top,
            bottom: placement.top + height,
        };
    }

    function rectsOverlap(a, b) {
        return a.left < b.right
            && a.right > b.left
            && a.top < b.bottom
            && a.bottom > b.top;
    }

    /* ONE SEAT SOLVE A FRAME. When AudioSettings owns the Return seat it
       already solves it on its own watch (the body's own children and class,
       End Turn, the Return control, the hands, the portraits) and on the
       public `moonfall:status-layout-changed` signal, coalescing every
       request of a frame into one solve. The tutorial's watch sees most of the
       same changes, and solved the seat again in the same frame: traced 28
       September 2026 on the guest tutorial's first screen (Chrome phone, 4x
       CPU), the owner's 0.42 s and the tutorial's 0.61 s of solves were
       largely the same frames. So the tutorial ASKS the owner, and keeps its
       own frame for what only it places — the coach mark — solving the seat
       itself there only when the coach mark moved, so the two are never
       painted a frame apart. */
    function seatOwnerSolves() {
        return typeof AudioSettings?.reposition === 'function';
    }

    function tipPlacementKey() {
        return tipElement ? `${tipElement.style.left}|${tipElement.style.top}` : '';
    }

    function scheduleSkipButtonPosition() {
        if (skipButton && seatOwnerSolves()) {
            document.dispatchEvent(new Event('moonfall:status-layout-changed'));
        }
        if (skipButtonPositionRaf) return;
        const raf = typeof window.requestAnimationFrame === 'function'
            ? window.requestAnimationFrame.bind(window)
            : (cb) => setTimeout(cb, 16);
        skipButtonPositionRaf = raf(() => {
            skipButtonPositionRaf = 0;
            let tipMoved = false;
            if (tipElement && activeTipTargetId && activeTipPosition !== 'center') {
                const before = tipPlacementKey();
                positionTipNearElement(activeTipTargetId, activeTipPosition);
                tipMoved = tipPlacementKey() !== before;
            }
            if (seatOwnerSolves() && !tipMoved) return;
            if (!positionSkipButton() && skipButton && skipButtonPositionRetries < 20
                && !skipButtonPositionRetryTimer) {
                skipButtonPositionRetries++;
                skipButtonPositionRetryTimer = setTimeout(() => {
                    skipButtonPositionRetryTimer = null;
                    scheduleSkipButtonPosition();
                }, 50);
            }
        });
    }

    function ensureSkipButtonObservedElements() {
        if (!skipButtonResizeObserver) return;
        skipButtonObservedElements.forEach((el) => {
            if (el?.isConnected) return;
            skipButtonResizeObserver.unobserve?.(el);
            skipButtonObservedElements.delete(el);
        });
        const observed = [
            document.getElementById('end-turn-btn'),
            skipButton,
            ...document.querySelectorAll(
                '.tutorial-tip, #mulligan-overlay, #mulligan-cards, '
                + '.mulligan-card-wrapper, .mulligan-header, #mulligan-confirm-btn, '
                + '#story-feed-popup, #audio-volume-control, #player-portrait, #opponent-portrait'
            ),
        ];
        observed.forEach((el) => {
            if (el && !skipButtonObservedElements.has(el)) {
                skipButtonResizeObserver.observe(el);
                skipButtonObservedElements.add(el);
            }
        });
    }

    /* WHAT THE BODY-WIDE WATCH SEES THAT CANNOT MOVE THE SEAT OR A TIP.
       Each of these used to re-run the whole seat solve — a forced style and
       layout pass of the battle, 30-55 ms on a 4x-throttled phone — about once
       a frame while the board's art arrived: 0.6 s of the guest tutorial's
       first screen went to it (traced 28 September 2026). None can move
       anything the solve measures:
       - anything inside a card: `.card` is a fixed box (cards.css width and
         height, overflow hidden), so its art layers, title fit and states
         never change its size;
       - a card back's paint canvas, laid over its pile or card (`position:
         absolute; inset: 0`, card-back-raster.js);
       - the cue layer the tutorial draws itself, a portrait's sampling probe,
         the audio control's own insides (its box is observed) and the lobby
         under the battle;
       - the mulligan clock's digits (its seat is pinned to End Turn's
         column; a tick re-ran the solve every second of the mulligan) and the
         boot veil lifting;
       - a node already taken out of the page. */
    const SEAT_INERT_SUBTREES = '#tutorial-cue-layer, .portrait-source-probe, #audio-volume-control, #lobby-view, .mulligan-timer-value, #app-loading';

    function isPaintOnlyNode(node) {
        return node?.nodeType === 1 && node.tagName === 'CANVAS' && node.dataset?.cardBackRaster === 'true';
    }

    function mutationCanMoveSeat(record) {
        const target = record?.target;
        const node = target?.nodeType === 1 ? target : target?.parentElement;
        if (!node || typeof node.closest !== 'function') return true;
        if (!node.isConnected) return false;
        if (node.closest(SEAT_INERT_SUBTREES)) return false;
        if (node.parentElement?.closest('.card')) return false;
        if (record.type === 'childList') {
            const changed = [...(record.addedNodes || []), ...(record.removedNodes || [])];
            if (changed.length && changed.every(isPaintOnlyNode)) return false;
        }
        return true;
    }

    function installSkipButtonPositionWatchers() {
        if (skipButtonPositionWatchersInstalled) return;
        skipButtonPositionWatchersInstalled = true;

        window.addEventListener('resize', scheduleSkipButtonPosition, { passive: true });
        skipButtonPositionDocumentEvents.forEach((eventName) => {
            document.addEventListener(eventName, scheduleSkipButtonPosition);
        });

        if (typeof ResizeObserver === 'function') {
            skipButtonResizeObserver = new ResizeObserver(scheduleSkipButtonPosition);
            ensureSkipButtonObservedElements();
        }
        if (typeof MutationObserver === 'function') {
            skipButtonMutationObserver = new MutationObserver((records) => {
                if (Array.isArray(records) && !records.some(mutationCanMoveSeat)) return;
                ensureSkipButtonObservedElements();
                scheduleSkipButtonPosition();
            });
            skipButtonMutationObserver.observe(document.body, {
                attributes: true,
                attributeFilter: ['class', 'hidden'],
                childList: true,
                subtree: true,
            });
        }
    }

    function uninstallSkipButtonPositionWatchers() {
        if (!skipButtonPositionWatchersInstalled) return;
        window.removeEventListener('resize', scheduleSkipButtonPosition);
        skipButtonPositionDocumentEvents.forEach((eventName) => {
            document.removeEventListener(eventName, scheduleSkipButtonPosition);
        });
        skipButtonResizeObserver?.disconnect?.();
        skipButtonResizeObserver = null;
        skipButtonMutationObserver?.disconnect?.();
        skipButtonMutationObserver = null;
        skipButtonObservedElements.clear();

        if (skipButtonPositionRaf) {
            if (typeof window.cancelAnimationFrame === 'function') {
                window.cancelAnimationFrame(skipButtonPositionRaf);
            } else {
                clearTimeout(skipButtonPositionRaf);
            }
            skipButtonPositionRaf = 0;
        }
        if (skipButtonPositionRetryTimer) {
            clearTimeout(skipButtonPositionRetryTimer);
            skipButtonPositionRetryTimer = null;
        }
        skipButtonPositionRetries = 0;
        skipButtonPositionWatchersInstalled = false;
    }

    /**
     * Reuse the one in-game exit control (Exit Match). Lobby owns activation so
     * tutorial start/end never adds duplicate listeners or replaces the node.
     */
    function tutorialText(key, fallback) {
        try {
            const value = i18n?.t?.(key);
            return value && value !== key ? value : fallback;
        } catch (_) {
            return fallback;
        }
    }

    function createSkipButton() {
        if (skipButton) return;

        skipButton = document.getElementById('skip-tutorial-btn');
        if (!skipButton) return;
        skipButton.style.visibility = 'hidden';
        // A guest tutorial reframes this control as "Skip Tutorial"; a signed-in
        // tutorial leaves it as "Exit Match". Lobby owns the wording.
        Lobby?.syncReturnControlLabel?.();
        installSkipButtonPositionWatchers();
        ensureSkipButtonObservedElements();
        positionSkipButton();
        scheduleSkipButtonPosition();
    }

    /**
     * End the tutorial (skip or complete)
     */
    function end() {
        // Only count an end that follows a start we were actually running.
        if (isActive) window.MoonfallFunnel?.track?.('tutorial_ended');
        runToken++;
        isActive = false;
        currentStep = 0;

        endIntentLesson();
        hideTip();
        clearForcedMoveRestrictions();
        TutorialCues?.destroy?.();
        uninstallSkipButtonPositionWatchers();

        skipButton = null;
        // The guest run has already deactivated by the time this fires, so the
        // shared control reverts to "Exit Match" for the next match.
        Lobby?.syncReturnControlLabel?.();
        AudioSettings?.reposition?.();

        // Update game state
        if (typeof GameState !== 'undefined' && GameState?.update) {
            GameState.update({ isTutorial: false, tutorialStep: -1 });
        }

        console.log('[Tutorial] Ended');
    }

    /* WHETHER THIS PLAYER HAS FINISHED THE TUTORIAL, as this browser saw it.
       Set when the server reports the scripted lesson complete (guest or
       signed in), so the Start Adventure sheet stops advising "Play the
       tutorial first" to someone who just played it. A hint, not an
       entitlement: private mode or cleared storage only brings the advice
       back. */
    const COMPLETED_KEY = 'moonfall_tutorial_completed';

    function rememberCompleted() {
        try { window.localStorage.setItem(COMPLETED_KEY, 'true'); } catch (_) { /* private mode: fine */ }
    }

    function hasCompleted() {
        try { return window.localStorage.getItem(COMPLETED_KEY) === 'true'; } catch (_) { return false; }
    }

    /**
     * Check if tutorial is active
     */
    function getIsActive() {
        return isActive;
    }

    /**
     * Get current step
     */
    function getStep() {
        return currentStep;
    }

    // Public API
    return {
        init,
        start,
        advance,
        end,
        onGameEvent,
        setTutorialData,
        getData: () => tutorialData,
        isActive: getIsActive,
        getStep,
        completeRecordedPhase,
        rememberCompleted,
        hasCompleted,
        applyForcedMoveRestrictions,
        clearForcedMoveRestrictions,
        refreshCues
    };
})();

export { Tutorial };
